import classNames from 'classnames'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useSwipeable } from 'react-swipeable'

import { navigateTo } from '../../../common/frontend/route-utils.js'
import { t } from '../../../common/i18n.js'
import {
    SidebarCollapseIcon,
    SidebarExpandIcon,
    SidebarPanelIcon,
} from '../../components/icon/icon.js'
import { CalendarPlus, File, MessageSquarePlus } from '../../components/icon/solar-icons.js'
import { Link } from '../../components/link/link.js'
import { PortalBookTimeAwayModal } from '../../components/portal-book-time-away-modal/portal-book-time-away-modal.js'
import '../../components/portal-bottom-nav/portal-bottom-nav.css'
import { PortalBottomNav } from '../../components/portal-bottom-nav/portal-bottom-nav.js'
import '../../components/portal-global-search/portal-global-search.css'
import { PortalGlobalSearch } from '../../components/portal-global-search/portal-global-search.js'
import { PortalHero } from '../../components/portal-hero/portal-hero.js'
import type { PortalHeroTab } from '../../components/portal-hero/portal-hero.js'
import '../../components/portal-impersonation-banner/portal-impersonation-banner.css'
import { PortalImpersonationBanner } from '../../components/portal-impersonation-banner/portal-impersonation-banner.js'
import '../../components/portal-mobile-drawer/portal-mobile-drawer.css'
import { PortalMobileDrawer } from '../../components/portal-mobile-drawer/portal-mobile-drawer.js'
import '../../components/portal-mobile-top-bar/portal-mobile-top-bar.css'
import { PortalMobileTopBar } from '../../components/portal-mobile-top-bar/portal-mobile-top-bar.js'
import type { ToastItem } from '../../components/portal-toast/portal-toast.js'
import { PortalToastContainer } from '../../components/portal-toast/portal-toast.js'
import type { HeroData } from '../../hero-data.js'
import {
    activeItemPath,
    isItemActive,
    navGroups,
    topTabs,
    visibleGroups,
    visibleTabs,
} from '../../nav-config.js'
import { navIcons } from '../../nav-icons.js'
import { canManageTimeAway, emptyPortalPermissions } from '../../portal-role.js'
import { subscribeToToasts } from '../../toast.js'
import { PortalRoleData, View } from '../../types.js'
import '../portal/portal-shell.css'
import { portalGet } from '../portal/utils/api.js'
import { fetchEmployees, stopImpersonation } from '../portal/utils/portal-api.js'
import './app-shell.scss'
import { registerLeaveBookingOpener } from './leave-booking-opener.js'
import { attachNavScrollReveal } from './nav-scroll-reveal.js'
import { sidebarCollapsedOnMeTransition } from './sidebar-collapse.js'

export interface AppShellProps {
    view: View
    heroData: HeroData
    portalRole: PortalRoleData | undefined
    children: ReactNode
}

export const AppShell = ({ view, heroData, portalRole, children }: AppShellProps) => {
    const currentPath = window.location.pathname
    const activePath = activeItemPath(navGroups, currentPath)
    const user = view.state.user
    const lang = view.state.lang

    // Portal permissions are lifted into global state and passed in as a prop.
    const permissions = portalRole?.permissions ?? emptyPortalPermissions()
    const myEmployeeId = portalRole?.myEmployeeId ?? null
    const googleDriveUrl = portalRole?.googleDriveUrl ?? null
    const myAvatarUrl = portalRole?.avatarUrl ?? null

    // App-level toasts — any view calls showToast() from the toast bus and the
    // single container lives here. Subscribe once on mount.
    const [toasts, setToasts] = useState<ToastItem[]>([])
    useEffect(() => subscribeToToasts((toast) => setToasts((prev) => [...prev, toast])), [])
    const dismissToast = (id: number) =>
        setToasts((prev) => prev.filter((toast) => toast.id !== id))

    // Headcount badge for People nav link
    const [workspaceHeadcount, setWorkspaceHeadcount] = useState<number | null>(null)

    // Pending time-away approvals badge for the "Kes on eemal" header tab.
    // Only managers (scope reports/everyone) can approve, so only they fetch it.
    const canManage = canManageTimeAway(portalRole?.timeAwayManageScope ?? null)
    const [pendingTimeAwayCount, setPendingTimeAwayCount] = useState<number | null>(null)

    // "View as" — warning banner shown while this session impersonates someone
    const impersonation = portalRole?.impersonation ?? null
    const [isExitingViewAs, setIsExitingViewAs] = useState(false)

    // Global search state
    const [isSearchOpen, setIsSearchOpen] = useState(false)

    // Leave booking modal state
    const [isLeaveBookingOpen, setIsLeaveBookingOpen] = useState(false)
    const [leaveBookingSuccessCallback, setLeaveBookingSuccessCallback] = useState<{
        fn?: () => void
    }>({})
    // Set when a manager/admin books on someone else's behalf; null = self.
    const [leaveBookingTarget, setLeaveBookingTarget] = useState<{
        id: number
        name: string
    } | null>(null)
    // Period prefill (e.g. a calendar click/drag); null = empty picker.
    const [leaveBookingInitialRange, setLeaveBookingInitialRange] = useState<{
        start: string
        end: string
    } | null>(null)

    useEffect(() => {
        const opener = (options?: {
            onSuccess?: () => void
            targetEmployee?: { id: number; name: string }
            initialRange?: { start: string; end: string }
        }) => {
            setIsLeaveBookingOpen(true)
            setLeaveBookingSuccessCallback({ fn: options?.onSuccess })
            setLeaveBookingTarget(options?.targetEmployee ?? null)
            setLeaveBookingInitialRange(options?.initialRange ?? null)
        }
        view.state._openLeaveBooking = opener
        registerLeaveBookingOpener(opener)
        return () => {
            delete view.state._openLeaveBooking
            registerLeaveBookingOpener(null)
        }
    }, [view])

    // Mobile drawer state
    const [drawerOpen, setDrawerOpen] = useState(false)

    // Desktop sidebar collapse (the sidebar is being phased out in favour of the
    // header tabs; collapsing is the interim step). localStorage holds the user's
    // explicit preference — only the toggle writes it; the /me auto-collapse below
    // never does.
    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        () => localStorage.getItem('portal-sidebar-collapsed') === '1',
    )
    const setSidebarCollapsedPersisted = (next: boolean) => {
        localStorage.setItem('portal-sidebar-collapsed', next ? '1' : '0')
        setSidebarCollapsed(next)
    }
    const toggleSidebar = () => setSidebarCollapsedPersisted(!sidebarCollapsed)

    // Entering a "Mina" detail view (/me, /me/*) collapses the sidebar — those
    // views carry a sticky tab bar that mirrors it. The collapse is transient: it
    // never overwrites the saved preference, and on leaving we restore whatever the
    // user had (open stays open, collapsed stays collapsed). Fire only on the /me
    // boundary crossing, so navigating between /me sub-pages doesn't re-collapse
    // and a manual toggle made while on /me survives the way back out. (TASK-274)
    const isMeView = currentPath === '/me' || currentPath.startsWith('/me/')
    const wasMeViewRef = useRef(false)
    useEffect(() => {
        const next = sidebarCollapsedOnMeTransition(
            isMeView,
            wasMeViewRef.current,
            localStorage.getItem('portal-sidebar-collapsed') === '1',
        )
        wasMeViewRef.current = isMeView
        if (next !== null) setSidebarCollapsed(next)
    }, [isMeView])

    // Swipe handlers for mobile drawer
    const swipeHandlers = useSwipeable({
        onSwipedRight: (e) => {
            // Only open drawer if swipe started near left edge
            if ((e.event as TouchEvent).changedTouches?.[0]?.clientX !== undefined) {
                setDrawerOpen(true)
            }
        },
        onSwipedLeft: () => {
            if (drawerOpen) setDrawerOpen(false)
        },
        delta: 50,
        trackTouch: true,
        trackMouse: false,
    })

    const handleLanguageToggle = useCallback(() => {
        const nextLang = lang === 'et' ? 'en' : 'et'
        view.state.lang = nextLang
        view.update()
    }, [lang, view])

    // ⌘K / Ctrl+K keyboard shortcut for global search
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                setIsSearchOpen(true)
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [])

    // Load workspace headcount for the People nav badge.
    // The badge is a fixed active count and does not react to any page filter.
    useEffect(() => {
        void (async () => {
            const res = await fetchEmployees({ limit: 1, status: 'active' })
            if (res.ok) setWorkspaceHeadcount(res.total)
        })()
    }, [])

    // Load the count of pending time-away approvals for the header tab badge.
    // Keyed on currentPath so the badge refreshes after a manager acts on
    // requests on /calendar and navigates away.
    useEffect(() => {
        if (!canManage) {
            setPendingTimeAwayCount(null)
            return
        }
        void (async () => {
            const res = await portalGet<{ id: number }[]>('/api/portal/time-off/pending')
            if (res.ok) setPendingTimeAwayCount(res.data.length)
        })()
    }, [canManage, currentPath])

    // The sidebar links self-navigate via <Link>. We only intercept a click on
    // the route we're already on: re-clicking is an in-place refresh, not a
    // navigation, so we suppress it to avoid a duplicate history entry. A
    // sub-page like /people/123 is NOT "on" /people — let that navigate.
    const handleNavClick = (path: string) => (e: React.MouseEvent) => {
        if (window.location.pathname === path) {
            e.preventDefault()
            return
        }
    }

    // Exiting "View as" returns the admin to the picker they started from.
    // Full reload (not SPA navigation) so all cached state is rebuilt for the
    // restored identity.
    const handleExitViewAs = async () => {
        setIsExitingViewAs(true)
        try {
            const result = await stopImpersonation()
            if (result.ok) {
                window.location.href = '/settings/view-as'
                return
            }
        } finally {
            setIsExitingViewAs(false)
        }
    }

    // Desktop sidebar nav is driven by the pure nav-config module.
    const groups = visibleGroups(navGroups, permissions)

    // Header navigation tabs (the redesigned top bar's primary navigation).
    // Permission-filtered the same way as the sidebar groups, with icons mapped
    // by path.
    const headerTabs = visibleTabs(topTabs, permissions)
    const tabs: PortalHeroTab[] = headerTabs.map((tab) => ({
        path: tab.path,
        label: tab.label(lang),
        icon: navIcons[tab.path] ?? <File size={18} weight="BoldDuotone" />,
        isActive: isItemActive(tab, currentPath),
        badgeCount: tab.path === '/calendar' ? (pendingTimeAwayCount ?? undefined) : undefined,
    }))

    // Global search shows the same header destinations (labels + gating) so it
    // never drifts from the rest of the app.
    const searchShortcuts = headerTabs.map((tab) => ({ label: tab.label, url: tab.path }))

    return (
        <div className="app-shell" {...swipeHandlers}>
            {/* "View as" warning banner — pinned above everything while active */}
            {impersonation && (
                <PortalImpersonationBanner
                    lang={lang}
                    viewingAsName={user?.name ?? ''}
                    isExiting={isExitingViewAs}
                    onExit={() => void handleExitViewAs()}
                />
            )}

            {/* Mobile top bar (visible < 768px) */}
            <PortalMobileTopBar
                lang={lang}
                userName={user?.name ?? t.common.userFallback(lang)}
                avatarUrl={myAvatarUrl}
                accountLinks={heroData.accountLinks}
                onMenuToggle={() => setDrawerOpen((prev) => !prev)}
                onSearchOpen={() => setIsSearchOpen(true)}
                onLogout={heroData.onLogout}
            />

            {/* Mobile drawer (visible < 768px) */}
            <PortalMobileDrawer
                isOpen={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                userName={user?.name ?? t.common.userFallback(lang)}
                userRole={user?.role ?? 'user'}
                avatarUrl={myAvatarUrl}
                lang={lang}
                activePath={activePath}
                groups={groups}
                quickLinks={heroData.quickLinks}
                googleDriveUrl={googleDriveUrl}
                myEmployeeId={myEmployeeId}
                onLanguageToggle={handleLanguageToggle}
                onFeedbackOpen={() => view.state._openFeedback?.()}
                themePreference={heroData.themePreference}
                onSelectTheme={heroData.onSelectTheme}
                themeLabels={heroData.themeLabels}
            />

            {/* Global top bar — header tabs + links/search/avatar
                (hidden < 768px, where the mobile top bar takes over). */}
            <div className="app-shell__top-bar">
                <PortalHero
                    {...heroData}
                    brandLabel="Hub"
                    homeHref="/"
                    tabs={tabs}
                    linksLabel={t.portal.nav.links(lang)}
                    onSearchOpen={() => setIsSearchOpen(true)}
                />
            </div>

            {/* Body row — sidebar + main content outlet */}
            <div className="app-shell__body portal-shell">
                {/* Sidebar (desktop only, hidden < 768px). Search,
                    notifications, and the profile/user menu now live in the
                    global top bar, so the sidebar starts directly with the nav. */}
                <aside
                    className={classNames(
                        'portal-shell__sidebar',
                        sidebarCollapsed && 'is-collapsed',
                    )}
                >
                    {(() => {
                        const toggleLabel = sidebarCollapsed
                            ? t.common.shell.openSidebar(lang)
                            : t.common.shell.closeSidebar(lang)
                        return (
                            <button
                                type="button"
                                className="portal-shell__sidebar-toggle"
                                onClick={toggleSidebar}
                                aria-label={toggleLabel}
                                aria-expanded={!sidebarCollapsed}
                            >
                                {/* Resting state: the bare panel glyph. On hover it swaps to a
                                    panel-with-chevron pointing the way the sidebar will move
                                    (left = collapse when open, right = expand when closed) and
                                    reveals the label tooltip. */}
                                <SidebarPanelIcon className="portal-shell__sidebar-toggle-icon" />
                                {sidebarCollapsed ? (
                                    <SidebarExpandIcon className="portal-shell__sidebar-toggle-arrow" />
                                ) : (
                                    <SidebarCollapseIcon className="portal-shell__sidebar-toggle-arrow" />
                                )}
                                <span className="portal-shell__sidebar-toggle-tip">
                                    {toggleLabel}
                                </span>
                            </button>
                        )
                    })()}

                    {/* Main navigation */}
                    <nav className="portal-shell__nav" ref={attachNavScrollReveal}>
                        {groups.map((group, groupIndex) => (
                            <div
                                key={group.label?.(lang) ?? groupIndex}
                                className="portal-shell__nav-section"
                            >
                                {group.label && (
                                    <div className="portal-shell__nav-section-header">
                                        <span className="portal-shell__nav-section-label">
                                            {group.label(lang)}
                                        </span>
                                    </div>
                                )}
                                {group.items.map((item) => {
                                    const count =
                                        item.path === '/people' ? workspaceHeadcount : null

                                    if (item.external) {
                                        if (item.path === 'google-drive-external') {
                                            if (!googleDriveUrl) return null
                                            const href = /^https?:\/\//i.test(googleDriveUrl)
                                                ? googleDriveUrl
                                                : `https://${googleDriveUrl}`
                                            return (
                                                <Link
                                                    key={item.path}
                                                    href={href}
                                                    external
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="portal-shell__nav-link"
                                                >
                                                    <span className="portal-shell__nav-icon">
                                                        {navIcons[item.path] ?? (
                                                            <File size={18} weight="BoldDuotone" />
                                                        )}
                                                    </span>
                                                    {item.label(lang)}
                                                    <span className="portal-shell__nav-external">
                                                        ↗
                                                    </span>
                                                </Link>
                                            )
                                        }
                                        return null
                                    }

                                    return (
                                        <Link
                                            key={item.path}
                                            href={item.path}
                                            className="portal-shell__nav-link"
                                            isActive={isItemActive(item, window.location.pathname)}
                                            onClick={handleNavClick(item.path)}
                                        >
                                            <span className="portal-shell__nav-icon">
                                                {navIcons[item.path] ?? (
                                                    <File size={18} weight="BoldDuotone" />
                                                )}
                                            </span>
                                            {item.label(lang)}
                                            {count !== null && (
                                                <span className="portal-shell__nav-count portal-shell__nav-count--muted">
                                                    {count}
                                                </span>
                                            )}
                                        </Link>
                                    )
                                })}
                            </div>
                        ))}
                    </nav>

                    {/* Sidebar footer — anchored to the bottom. Used for
                        secondary actions (Tagasiside / Feedback) that used to
                        live in the top-right hero. */}
                    <div className="portal-shell__sidebar-footer">
                        <button
                            type="button"
                            className="portal-shell__nav-link"
                            onClick={() => view.state._openFeedback?.()}
                            aria-label={t.feedback.triggerAriaLabel(lang)}
                        >
                            <span className="portal-shell__nav-icon">
                                <MessageSquarePlus size={18} />
                            </span>
                            {t.feedback.triggerLabel(lang)}
                        </button>
                    </div>
                </aside>

                {/* Main content outlet */}
                <main className="portal-shell__main">{children}</main>
            </div>

            {/* Bottom nav (visible < 768px) */}
            <PortalBottomNav
                activePath={activePath}
                lang={lang}
                onMoreClick={() => setDrawerOpen(true)}
            />

            {/* Global search overlay */}
            <PortalGlobalSearch
                isOpen={isSearchOpen}
                lang={lang}
                shortcuts={searchShortcuts}
                onClose={() => setIsSearchOpen(false)}
                onNavigate={(path) => {
                    navigateTo(path)
                    setIsSearchOpen(false)
                }}
            />

            {/* App-level toast notifications (any view produces these via the toast bus) */}
            <PortalToastContainer lang={lang} toasts={toasts} onDismiss={dismissToast} />

            {/* Centralized leave booking modal */}
            <PortalBookTimeAwayModal
                isOpen={isLeaveBookingOpen}
                onClose={() => setIsLeaveBookingOpen(false)}
                myEmployeeId={myEmployeeId}
                targetEmployee={leaveBookingTarget}
                initialRange={leaveBookingInitialRange}
                lang={lang}
                onSuccess={() => leaveBookingSuccessCallback.fn?.()}
            />

            {/* Floating button "Book time away" */}
            {myEmployeeId && (
                <button
                    type="button"
                    className="portal-book-time-away-fab"
                    onClick={() => {
                        setLeaveBookingTarget(null)
                        setLeaveBookingInitialRange(null)
                        setIsLeaveBookingOpen(true)
                    }}
                    aria-label={t.portal.timeOff.bookTimeAway(lang)}
                >
                    <CalendarPlus size={18} weight="BoldDuotone" />
                    <span className="portal-book-time-away-fab__label">
                        {t.portal.timeOff.bookTimeAway(lang)}
                    </span>
                </button>
            )}
        </div>
    )
}
